'use client';

import React, { useState } from 'react';
import { Search, PieChart } from 'lucide-react';
import type { Deduction, PayPeriod } from '../page';

interface DeductionTrackingProps {
  deductions: Deduction[];
  period: PayPeriod;
}

const roleBadge: Record<string, string> = {
  RN: 'badge-rn',
  CNA: 'badge-cna',
  LPN: 'badge-lpn',
  NP: 'badge-np',
  MD: 'badge-md',
};

export default function DeductionTracking({ deductions, period }: DeductionTrackingProps) {
  const [search, setSearch] = useState('');

  const filtered = deductions.filter((d) =>
    d.staffName.toLowerCase().includes(search.toLowerCase())
  );

  const fmt = (n: number) => `$${n.toLocaleString()}`;

  const totals = filtered.reduce(
    (acc, d) => ({
      federalTax: acc.federalTax + d.federalTax,
      stateTax: acc.stateTax + d.stateTax,
      socialSecurity: acc.socialSecurity + d.socialSecurity,
      medicare: acc.medicare + d.medicare,
      healthInsurance: acc.healthInsurance + d.healthInsurance,
      retirement401k: acc.retirement401k + d.retirement401k,
      other: acc.other + d.other,
      totalDeductions: acc.totalDeductions + d.totalDeductions,
      grossPay: acc.grossPay + d.grossPay,
    }),
    { federalTax: 0, stateTax: 0, socialSecurity: 0, medicare: 0, healthInsurance: 0, retirement401k: 0, other: 0, totalDeductions: 0, grossPay: 0 }
  );

  // Breakdown summary cards
  const breakdownItems = [
    { label: 'Federal Tax', value: totals.federalTax, color: 'bg-danger/10 text-danger', pct: totals.grossPay > 0 ? ((totals.federalTax / totals.grossPay) * 100).toFixed(1) : '0' },
    { label: 'State Tax', value: totals.stateTax, color: 'bg-warning/10 text-warning', pct: totals.grossPay > 0 ? ((totals.stateTax / totals.grossPay) * 100).toFixed(1) : '0' },
    { label: 'Social Security', value: totals.socialSecurity, color: 'bg-info/10 text-info', pct: totals.grossPay > 0 ? ((totals.socialSecurity / totals.grossPay) * 100).toFixed(1) : '0' },
    { label: 'Medicare', value: totals.medicare, color: 'bg-primary/10 text-primary', pct: totals.grossPay > 0 ? ((totals.medicare / totals.grossPay) * 100).toFixed(1) : '0' },
    { label: 'Health Insurance', value: totals.healthInsurance, color: 'bg-positive/10 text-positive', pct: totals.grossPay > 0 ? ((totals.healthInsurance / totals.grossPay) * 100).toFixed(1) : '0' },
    { label: '401(k) Retirement', value: totals.retirement401k, color: 'bg-accent/10 text-accent', pct: totals.grossPay > 0 ? ((totals.retirement401k / totals.grossPay) * 100).toFixed(1) : '0' },
  ];

  return (
    <div className="space-y-4">
      {/* Breakdown summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {breakdownItems.map((item) => (
          <div key={item.label} className="bg-card border border-border rounded-xl p-4">
            <div className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-600 uppercase tracking-wide mb-2 ${item.color}`}>
              {item.pct}% of gross
            </div>
            <p className="text-lg font-700 text-foreground tabular-nums">{fmt(item.value)}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{item.label}</p>
          </div>
        ))}
      </div>

      {/* Deduction table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="flex items-center gap-3 p-4 border-b border-border flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search staff…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
            <PieChart size={13} />
            <span>{filtered.length} staff members</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap">Staff Member</th>
                <th className="px-4 py-3 text-left text-xs font-600 text-muted-foreground uppercase tracking-wide">Role</th>
                <th className="px-4 py-3 text-right text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap">Gross Pay</th>
                <th className="px-4 py-3 text-right text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap">Federal Tax</th>
                <th className="px-4 py-3 text-right text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap">State Tax</th>
                <th className="px-4 py-3 text-right text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap">Soc. Security</th>
                <th className="px-4 py-3 text-right text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap">Medicare</th>
                <th className="px-4 py-3 text-right text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap">Health Ins.</th>
                <th className="px-4 py-3 text-right text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap">401(k)</th>
                <th className="px-4 py-3 text-right text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap">Total Ded.</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.id} className="border-b border-border row-hover transition-colors">
                  <td className="px-4 py-3 font-500 text-foreground whitespace-nowrap">{d.staffName}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-600 ${roleBadge[d.role] ?? 'bg-muted text-muted-foreground'}`}>
                      {d.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-500">{fmt(d.grossPay)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-danger">{fmt(d.federalTax)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-warning">{fmt(d.stateTax)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-info">{fmt(d.socialSecurity)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-primary">{fmt(d.medicare)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-positive">{fmt(d.healthInsurance)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-accent">{fmt(d.retirement401k)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-700 text-foreground">{fmt(d.totalDeductions)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-muted/60 border-t-2 border-border">
                <td className="px-4 py-3 font-700 text-foreground" colSpan={2}>Totals</td>
                <td className="px-4 py-3 text-right tabular-nums font-700">{fmt(totals.grossPay)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-700 text-danger">{fmt(totals.federalTax)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-700 text-warning">{fmt(totals.stateTax)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-700 text-info">{fmt(totals.socialSecurity)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-700 text-primary">{fmt(totals.medicare)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-700 text-positive">{fmt(totals.healthInsurance)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-700 text-accent">{fmt(totals.retirement401k)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-700 text-foreground">{fmt(totals.totalDeductions)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
